import buildQuery, { extractQSAsObject } from 'utils/queryStrings';
import createTransport from 'api/transport/createTransport';
import factory from 'state/factory';
import trimAmpWebHost from 'utils/trimAmpWebHost';
import UrlParse from 'url-parse';
import { concatTitleAndVersion } from 'utils/trackFormatter';
import { getAlbumUrl, getArtistUrl, getLiveUrl, getTrackUrl } from 'utils/url';
import { getPodcastUrl } from 'state/Podcast/helpers';
import { SEARCH_FILTERS } from 'state/Search/constants';
import { searchRequest } from 'state/Search/services';
import { slugify } from 'utils/string';
import { STATION_TYPE } from 'constants/stationTypes';
import { THUMB_RES } from 'components/MediaServerImage';

const transport = createTransport(factory);

const KEYWORD_TYPE_MAP = {
  custom: 'artist',
  live: 'live',
  podcast: 'podcast',
  talk: 'podcast', // Needed for 'BestMatch' results which comes from AMP and may have a type of 'talk' instead of 'podcast' [WEB-8933]
  track: 'track',
};

const bestMatchTypes = {
  ALBUM: SEARCH_FILTERS.ALBUM,
  ARTIST: SEARCH_FILTERS.ARTIST,
  COLLECTION: SEARCH_FILTERS.COLLECTION,
  PLAYLIST: SEARCH_FILTERS.COLLECTION,
  PODCAST: SEARCH_FILTERS.PODCAST,
  STATION: SEARCH_FILTERS.LIVE,
  TRACK: SEARCH_FILTERS.SONG,
};

function _process(filterType, { bestMatch, results } = {}, profileId, isAnonymous = true) {
  if (!bestMatch && !results) return {};
  const {
    artists = [],
    tracks = [],
    stations = [],
    playlists = [],
    keywords = [],
    podcasts = [],
    albums = [],
  } = results;
  const result = {
    total: Object.keys(results).reduce((count, key) => count + results[key].length, 0),
    [SEARCH_FILTERS.ARTIST]: {
      data: artists.map(({ id, name }) => ({
        catalogId: id,
        catalogType: STATION_TYPE.ARTIST,
        id,
        imgWidth: THUMB_RES,
        title: name,
        url: getArtistUrl(id, name),
      })),
      total: artists.length,
    },
    [SEARCH_FILTERS.ALBUM]: {
      data: albums.map(
        ({ id, title, artistName, artistId, playbackRights = {}, explicitLyrics }) => ({
          artistId,
          artistName,
          catalogId: id,
          catalogType: STATION_TYPE.ALBUM,
          description: artistName,
          descriptionUrl: getArtistUrl(artistId, artistName),
          explicitLyrics,
          id,
          imgWidth: THUMB_RES,
          isOnDemandTrack: playbackRights.onDemand,
          title,
          url: getAlbumUrl(artistId, artistName, id, title),
        })
      ),
      total: albums.length,
    },
    [SEARCH_FILTERS.SONG]: {
      data: tracks.map(
        ({ id, title, artistName, artistId, playbackRights = {}, explicitLyrics, version }) => ({
          artistId,
          artistName,
          catalogId: id,
          catalogType: STATION_TYPE.TRACK,
          description: artistName,
          descriptionUrl: getArtistUrl(artistId, artistName),
          explicitLyrics,
          id,
          imgWidth: THUMB_RES,
          isOnDemandTrack: playbackRights.onDemand,
          title: concatTitleAndVersion(title, version),
          url: getTrackUrl(artistId, artistName, id, title),
        })
      ),
      total: tracks.length,
    },
    [SEARCH_FILTERS.LIVE]: {
      data: stations.map(
        ({ id, callLetters, name, description, imageUrl: imgUrl = getLiveUrl(id, name) }) => ({
          callLetters,
          catalogId: id,
          catalogType: STATION_TYPE.LIVE,
          description,
          id,
          imgUrl,
          imgWidth: THUMB_RES,
          title: name,
          url: getLiveUrl(id, name),
        })
      ),
      total: stations.length,
    },
    [SEARCH_FILTERS.PODCAST]: {
      data: podcasts.map(({ id, title, description }) => ({
        catalogId: id,
        catalogType: STATION_TYPE.PODCAST,
        description,
        id,
        imgWidth: THUMB_RES,
        title,
        url: getPodcastUrl(id, slugify(title)), // New podcast calls no longer include slug. so we must slugify the title to get the url for [WEB-8933]
      })),
      total: podcasts.length,
    },
    [SEARCH_FILTERS.COLLECTION]: {
      data: playlists.map(({ id, userId, urls, name, author, description }) => ({
        author,
        catalogId: id,
        catalogType: STATION_TYPE.COLLECTION,
        catalogUserId: userId,
        description,
        id,
        imgUrl: urls.image,
        imgWidth: THUMB_RES,
        isOnDemandTrack: true,
        title: name,
        url: trimAmpWebHost(urls.web),
      })),
      total: playlists.length,
    },
  };

  let displayBestMatch;

  if (bestMatch && bestMatch.format === 'KEYWORDS' && !filterType) {
    const keyword = keywords.find(kw => kw.id === bestMatch.id);
    let url = trimAmpWebHost(keyword.webUrl);
    const favLink = isAnonymous
      ? `/content/how-to-create-the-most-awesome-radio-station-ever-with-my-favorites-radio/?cmp=ihr-favorites-noteligible`
      : `${url}${profileId}/`;

    // if type === link, we shouldn't ever autoplay. remove if it exists in query params
    if (keyword.contentType === 'link') {
      const { href } = UrlParse(url);
      const queryString = extractQSAsObject(url);
      delete queryString.autoplay;
      url = `${href.split('?')[0]}${buildQuery(queryString)}`;
    }

    displayBestMatch = {
      catalogId: KEYWORD_TYPE_MAP[keyword.contentType] ? keyword.contentId : undefined,
      catalogType: KEYWORD_TYPE_MAP[keyword.contentType],
      contentType: keyword.contentType,
      description: keyword.description,
      id: keyword.id,
      imgUrl: keyword.imageUrl,
      imgWidth: THUMB_RES,
      title: keyword.name,
      url: url.includes('/favorites/') ? favLink : url,
    };
  }

  if (bestMatch && bestMatch.format !== 'KEYWORDS' && !filterType) {
    const arrayWithBestMatch = Object.assign([], result[bestMatchTypes[bestMatch.format]].data);
    if (bestMatch.id) {
      displayBestMatch = arrayWithBestMatch.find(r => r.id === bestMatch.id);
    } else {
      displayBestMatch = arrayWithBestMatch[0]; // eslint-disable-line
    }

    result[bestMatchTypes[bestMatch.format]].data = arrayWithBestMatch.filter(
      resource => resource !== displayBestMatch
    );
  }

  result.bestMatch = !filterType && displayBestMatch ? [displayBestMatch] : [];
  return result;
}

export const search = function search(
  kw,
  filterType,
  opts,
  profileId,
  sessionId,
  ampUrl,
  isAnonymous,
  host,
  marketId
) {
  // 12/28/16
  // This is good for the current use case where we either want exactly one or all of the possible results
  // if we ever move into a more complex world where we can select exactly which sorts of results we want
  // we should look into using something like sets (either Immutable or ES6/2015)
  const resultFilters = Object.keys(SEARCH_FILTERS)
    .filter(type => type !== 'ALL')
    .reduce(
      (memo, key) => ({
        ...memo,
        [SEARCH_FILTERS[key]]: filterType ? filterType === SEARCH_FILTERS[key] : true,
      }),
      {}
    );

  const requestParams = {
    boostMarketId: marketId,
    keyword: true,
    keywords: kw,
    maxRows: 4,
    ...opts,
    ...resultFilters,
    bundle: resultFilters.bundle || SEARCH_FILTERS.ALL || filterType === SEARCH_FILTERS.ALBUM,
  };

  return transport(searchRequest({ ampUrl, host, params: requestParams, profileId, sessionId }))
    .then(response => {
      const { status, statusText, data } = response;

      if (status >= 200 && status < 300) {
        return data;
      }

      const error = new Error(statusText);
      error.response = response;
      throw error;
    })
    .then(data => _process(filterType, data, profileId, isAnonymous));
};
